import { AfterViewInit, Component, HostListener, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

interface Carousel {
  items: string[]; // rutas de imagen
  pos: number[]; // data-pos paralelo a items
  max: number;
}

const IMG = 'assets/img/sistema/';
const LBL = 'assets/img/etiquetas/';

// Vídeo "Caso de éxito"
const CASE_VIDEO_IDS: Record<string, string> = { es: 'KWetvzrSrIs', en: 'IiiFiUXC_M4' };
// Vídeo "¿Qué es Gesvatec?"
const INTRO_VIDEO_IDS: Record<string, string> = { es: 'GgzJ5gPw9lg', en: 'AJh4N3tngKw' };

function carousel(max: number, entries: Array<[number, string]>): Carousel {
  return { max, pos: entries.map((e) => e[0]), items: entries.map((e) => e[1]) };
}

@Component({
  selector: 'app-sistema-gesvatec',
  templateUrl: './sistema-gesvatec.component.html',
  styleUrls: ['./sistema-gesvatec.component.scss'],
})
export class SistemaGesvatecComponent implements AfterViewInit, OnDestroy {
  // Pestañas "¿Qué podrá hacer?" de la aplicación web
  activeUser: 'envasador' | 'entidad' = 'envasador';

  // Iconos de las listas (icon-item) por clave i18n paralela
  readonly envasadorIcons = [IMG + 'icons/icon_01.webp', IMG + 'icons/icon_02.webp', IMG + 'icons/icon_03.webp', IMG + 'icons/icon_04.webp'];
  readonly entidadIcons = [IMG + 'icons/icon_01.webp', IMG + 'icons/icon_05.webp', IMG + 'icons/icon_06.webp', IMG + 'icons/icon_07.webp'];
  readonly softwareItemIcons = [IMG + 'icons/icon_01.webp', IMG + 'icons/icon_02.webp'];
  readonly printers = IMG + 'printers/impresoras.webp';
  readonly apiImg = IMG + 'API.svg';

  // Carruseles coverflow
  gis = carousel(5, [[-4, 'gis/gis_07.webp'], [-3, 'gis/gis_08.webp'], [-2, 'gis/gis_09.webp'], [-1, 'gis/gis_10.webp'], [0, 'gis/gis_01.webp'], [1, 'gis/gis_02.webp'], [2, 'gis/gis_03.webp'], [3, 'gis/gis_04.webp'], [4, 'gis/gis_05.webp'], [5, 'gis/gis_06.webp']].map((e) => [e[0] as number, IMG + (e[1] as string)]));
  warnings = carousel(2, [[-2, 'warnings/warnings_04.webp'], [-1, 'warnings/warnings_05.webp'], [0, 'warnings/warnings_01.webp'], [1, 'warnings/warnings_02.webp'], [2, 'warnings/warnings_03.webp']].map((e) => [e[0] as number, IMG + (e[1] as string)]));
  statistics = carousel(5, [[-5, 'statistics/statistics_07.webp'], [-4, 'statistics/statistics_08.webp'], [-3, 'statistics/statistics_09.webp'], [-2, 'statistics/statistics_10.webp'], [-1, 'statistics/statistics_11.webp'], [0, 'statistics/statistics_01.webp'], [1, 'statistics/statistics_02.webp'], [2, 'statistics/statistics_03.webp'], [3, 'statistics/statistics_04.webp'], [4, 'statistics/statistics_05.webp'], [5, 'statistics/statistics_06.webp']].map((e) => [e[0] as number, IMG + (e[1] as string)]));
  royalties = carousel(4, [[-3, 'royalties/royalties_06.webp'], [-2, 'royalties/royalties_07.webp'], [-1, 'royalties/royalties_08.webp'], [0, 'royalties/royalties_01.webp'], [1, 'royalties/royalties_02.webp'], [2, 'royalties/royalties_03.webp'], [3, 'royalties/royalties_04.webp'], [4, 'royalties/royalties_05.webp']].map((e) => [e[0] as number, IMG + (e[1] as string)]));
  software = carousel(2, [[-3, 'etiquetado/etiquetado_01.webp'], [-2, 'etiquetado/etiquetado_02.webp'], [-1, 'etiquetado/etiquetado_03.webp'], [0, 'etiquetado/etiquetado_04.webp'], [1, 'etiquetado/etiquetado_05.webp'], [2, 'etiquetado/etiquetado_06.webp']].map((e) => [e[0] as number, IMG + (e[1] as string)]));
  labels = carousel(3, [[-2, 'Earlyna'], [-1, 'Leanri'], [0, 'M7'], [1, 'Nadorcott'], [2, 'Sando'], [3, 'SummerPrim']].map((e) => [e[0] as number, LBL + (e[1] as string) + '_Etiqueta_Final_Troquel_Textos.webp']));
  private labelsTimer?: ReturnType<typeof setInterval>;

  // Vídeos informativos
  introVideoUrl: SafeResourceUrl;
  readonly gisVideoUrl: SafeResourceUrl;
  readonly warningsVideoUrl: SafeResourceUrl;
  readonly statsVideoUrl: SafeResourceUrl;
  readonly royaltiesVideoUrl: SafeResourceUrl;

  // Modal "Caso de éxito"
  modalOpen = false;
  modalUrl: SafeResourceUrl | null = null;

  private readonly langSub: Subscription;

  constructor(private readonly sanitizer: DomSanitizer, private readonly translate: TranslateService) {
    const safe = (u: string) => this.sanitizer.bypassSecurityTrustResourceUrl(u);
    this.introVideoUrl = this.buildVideoUrl(INTRO_VIDEO_IDS, 'controls=1');
    this.gisVideoUrl = safe('https://www.youtube-nocookie.com/embed/Ne3YroKpQqY?rel=0&modestbranding=1&showinfo=0&controls=1&loop=1&playlist=Ne3YroKpQqY');
    this.warningsVideoUrl = safe('https://www.youtube-nocookie.com/embed/SWHNPM0MVD8?rel=0&modestbranding=1&showinfo=0&controls=1&loop=1&playlist=SWHNPM0MVD8');
    this.statsVideoUrl = safe('https://www.youtube-nocookie.com/embed/0Yyn-Lkm5t8?rel=0&modestbranding=1&showinfo=0&controls=1&loop=1&playlist=0Yyn-Lkm5t8');
    this.royaltiesVideoUrl = safe('https://www.youtube-nocookie.com/embed/SjXS-P5mqZ4?rel=0&modestbranding=1&showinfo=0&controls=1&loop=1&playlist=SjXS-P5mqZ4');
    
    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.introVideoUrl = this.buildVideoUrl(INTRO_VIDEO_IDS, 'controls=1');
      if (this.modalOpen) {
        this.modalUrl = this.buildVideoUrl(CASE_VIDEO_IDS, 'controls=1&autoplay=1');
      }
    });
  }

  ngAfterViewInit(): void {
    this.startLabelsAutoRotate();
  }

  private buildVideoUrl(ids: Record<string, string>, params: string): SafeResourceUrl {
    const id = ids[this.translate.currentLang] ?? ids['es'];
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1&showinfo=0&${params}`,
    );
  }

  scrollTo(id: string): void {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ---- Carruseles ----
  // Recoloca en circular: las posiciones quedan en [max - n + 1, max]
  center(c: Carousel, index: number): void {
    const active = c.pos[index];
    const n = c.pos.length;
    const min = c.max - n + 1;
    c.pos = c.pos.map((p) => ((((p - active - min) % n) + n) % n) + min);
  }
  nav(c: Carousel, dir: 'left' | 'right'): void {
    const sign = dir === 'right' ? 1 : -1;
    let k = c.pos.indexOf(sign);
    if (k < 0) k = c.pos.indexOf(sign * 2);
    if (k >= 0) this.center(c, k);
  }
  private startLabelsAutoRotate(): void {
    if (this.labelsTimer) return;
    this.labelsTimer = setInterval(() => {
      let k = this.labels.pos.indexOf(-1);
      if (k < 0) k = this.labels.pos.indexOf(-2);
      if (k >= 0) this.center(this.labels, k);
    }, 2500);
  }

  // ---- Modal vídeo ----
  openModal(): void {
    this.modalUrl = this.buildVideoUrl(CASE_VIDEO_IDS, 'controls=1&autoplay=1');
    this.modalOpen = true;
    document.body.classList.add('modal-active');
  }
  closeModal(): void {
    this.modalOpen = false;
    this.modalUrl = null;
    document.body.classList.remove('modal-active');
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.modalOpen) this.closeModal();
  }

  ngOnDestroy(): void {
    if (this.labelsTimer) clearInterval(this.labelsTimer);
    this.langSub.unsubscribe();
    document.body.classList.remove('modal-active');
  }
}
